import { useState, useEffect } from 'react'
import { useUser } from '@clerk/clerk-react'
import { getAdminSettings, patchAdminSettings, flushInferenceQueue, type AdminSettings } from '../lib/api'
import { useThemeColors } from '../hooks/useThemeColors'

const ADMIN_EMAILS = ['matt@primateintelligence.ai', 'mehdi@primateintelligence.ai']

// ── Toggle ────────────────────────────────────────────────────────────────────
function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  const c = useThemeColors()
  return (
    <button
      role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
      style={{ width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
        background: checked ? c.signal : c.carbon, position: 'relative', transition: 'background 150ms', flexShrink: 0 }}>
      {/* PRI-524 audit: thumb is white in both themes — textPrimary is near-black in light theme */}
      <span style={{ position: 'absolute', top: 2, left: checked ? 20 : 2, width: 18, height: 18,
        borderRadius: '50%', background: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 150ms' }} />
    </button>
  )
}

// ── Number setting row ────────────────────────────────────────────────────────
function NumberRow({
  label, description, displayValue, unit, min, max, step = 1, saving, saved,
  onDisplayChange, onSave,
}: {
  label: string; description: string; displayValue: string; unit?: string
  min: number; max: number; step?: number; saving: boolean; saved: boolean
  onDisplayChange: (v: string) => void; onSave: () => void
}) {
  const c = useThemeColors()
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '14px 0', borderBottom: `1px solid ${c.border}`, gap: 16 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: c.textPrimary, marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 12, color: c.textMuted }}>{description}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="number" min={min} max={max} step={step} value={displayValue}
          onChange={e => onDisplayChange(e.target.value)}
          style={{ width: 80, background: c.bgPrimary, border: `1px solid ${c.border}`, borderRadius: 3,
            padding: '6px 10px', fontSize: 13, color: c.textPrimary, textAlign: 'right' as const }} />
        {unit && <span style={{ fontSize: 12, color: c.textMuted, width: 30 }}>{unit}</span>}
        <button onClick={onSave} disabled={saving}
          style={{ background: saved ? '#16A34A' : c.signal, color: '#fff', border: 'none', borderRadius: 3,
            padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', minWidth: 52, opacity: saving ? 0.7 : 1 }}>
          {saving ? '…' : saved ? '✓' : 'Save'}
        </button>
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
export default function AdminAPISettings() {
  const { user, isLoaded } = useUser()
  const c = useThemeColors()
  const [settings, setSettings] = useState<AdminSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [savedKey, setSavedKey] = useState<string | null>(null)
  const [flushing, setFlushing] = useState(false)
  const [flushResult, setFlushResult] = useState<string | null>(null)

  async function handleFlush() {
    if (!confirm('This will kill all running inference jobs and clear the queue. The server stays up but all pending work is lost. Continue?')) return;
    setFlushing(true)
    setFlushResult(null)
    try {
      const res = await flushInferenceQueue()
      setFlushResult(`✓ Cleared ${res.queue_cleared ?? 0} queued + ${res.active_cancelled ?? 0} active jobs. Server ready.`)
      setTimeout(() => setFlushResult(null), 5000)
    } catch (e: unknown) {
      setFlushResult(`✗ ${e instanceof Error ? e.message : 'Failed'}`)
    } finally {
      setFlushing(false)
    }
  }

  // Display values (strings for input binding)
  const [displayVals, setDisplayVals] = useState<Record<string, string>>({})

  const isAdmin = isLoaded && user != null &&
    ADMIN_EMAILS.includes(user.primaryEmailAddress?.emailAddress ?? '')

  useEffect(() => {
    if (!isAdmin) return
    getAdminSettings()
      .then(s => {
        setSettings(s)
        // Initialise display values
        setDisplayVals({
          max_upload_bytes: String(Math.round((s.max_upload_bytes ?? 20971520) / 1024 / 1024)),
          free_max_duration_seconds: String(s.free_max_duration_seconds ?? 60),
          pro_max_duration_seconds: String(s.pro_max_duration_seconds ?? 1200),
          demo_analyses_per_day: String(s.demo_analyses_per_day ?? 3),
          video_sample_fps: String(((s.video_sample_fps ?? 60) / 10).toFixed(1)),
          frames_per_segment: String(s.frames_per_segment ?? 16),
          process_resolution: String(s.process_resolution ?? 256),
          mask_confidence_threshold: String(((s.mask_confidence_threshold ?? 50) / 100).toFixed(2)),
          narrative_threshold: String(s.narrative_threshold ?? 50),
          narrative_stride_frames: String(s.narrative_stride_frames ?? 16),
        })
        setLoading(false)
      })
      .catch(e => { setError(e.message); setLoading(false) })
  }, [isAdmin])

  async function save(key: string, rawValue: number) {
    setSavingKey(key)
    setError(null)
    try {
      await patchAdminSettings({ [key]: rawValue } as Partial<AdminSettings>)
      setSettings(prev => prev ? { ...prev, [key]: rawValue } : prev)
      setSavedKey(key)
      setTimeout(() => setSavedKey(null), 2000)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSavingKey(null)
    }
  }

  async function toggleMasks(on: boolean) {
    const val = on ? 1 : 0
    setSettings(prev => prev ? { ...prev, output_masks: val } : prev)
    await save('output_masks', val)
  }

  async function toggleNarrative(on: boolean) {
    const val = on ? 1 : 0
    setSettings(prev => prev ? { ...prev, narrative_enabled: val } : prev)
    await save('narrative_enabled', val)
  }

  if (!isLoaded) return <div className="min-h-screen bg-void" />
  if (!isAdmin) return (
    <div className="min-h-screen bg-void text-chalk flex items-center justify-center">
      <p className="text-body text-steel">Admin access required</p>
    </div>
  )

  const masksOn = (settings?.output_masks ?? 0) === 1
  const narrativeOn = (settings?.narrative_enabled ?? 0) === 1

  return (
    <div className="flex-1 p-8 text-chalk">
      <div style={{ maxWidth: 720 }}>
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: c.textPrimary, letterSpacing: '-0.01em', margin: '0 0 6px' }}>
            API Settings
          </h1>
          <p style={{ fontSize: 13, color: c.textMuted, margin: '0 0 12px' }}>
            Internal inference and upload configuration.
          </p>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'rgba(215,119,6,0.08)', border: '1px solid rgba(215,119,6,0.30)', borderLeft: '3px solid #D97706', borderRadius: 3, padding: '10px 14px' }}>
            <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>⚠️</span>
            <p style={{ fontSize: 12, color: c.textPrimary, margin: 0, lineHeight: 1.6 }}>
              <strong style={{ color: '#FCD34D' }}>These settings are global</strong> — changes affect all users immediately.
              Do not enable experimental settings during active usage without coordinating with Mehdi first.
            </p>
          </div>
        </div>

        {loading && <p style={{ color: c.textMuted, fontSize: 13 }}>Loading settings…</p>}
        {error && (
          <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 3,
            padding: '10px 14px', fontSize: 13, color: '#F87171', marginBottom: 20 }}>
            {error}
          </div>
        )}

        {settings && !loading && (
          <>
            {/* ── Inference settings ── */}
            <div style={{ background: c.bgSecondary, border: `1px solid ${c.border}`, borderRadius: 3, padding: '20px 24px', marginBottom: 20 }}>
              <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, letterSpacing: '0.12em',
                textTransform: 'uppercase' as const, color: c.signal, marginBottom: 16 }}>
                Inference — Model Settings
              </div>

              {/* Segmentation masks */}
              <div style={{ display: 'flex', alignItems: 'flex-start', padding: '14px 0', borderBottom: `1px solid ${c.border}`, gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: c.textPrimary, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                    Segmentation masks (SAM3)
                    {!masksOn && (
                      <span style={{ fontSize: 10, fontFamily: '"JetBrains Mono", monospace', letterSpacing: '0.06em',
                        textTransform: 'uppercase' as const, background: 'rgba(220,38,38,0.15)', color: '#F87171',
                        padding: '2px 8px', borderRadius: 3 }}>disabled — CUDA OOM</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: c.textMuted, lineHeight: 1.5 }}>
                    SAM3 segmentation overlay on result video. Currently disabled: JEPA model uses ~21GB of 22GB A10G VRAM,
                    leaving no room for SAM3. Enable when Mehdi fixes shared RADIO backbone (inference.py line 1324).
                  </div>
                </div>
                <Toggle checked={masksOn} onChange={toggleMasks} />
              </div>

              <NumberRow label="Video sample FPS" unit="fps" min={1} max={30} step={0.5}
                description="Frames per second for JEPA model sampling. Higher = more detail, slower inference. (default: 6.0)"
                displayValue={displayVals.video_sample_fps ?? '6.0'} saving={savingKey === 'video_sample_fps'} saved={savedKey === 'video_sample_fps'}
                onDisplayChange={v => setDisplayVals(d => ({ ...d, video_sample_fps: v }))}
                onSave={() => save('video_sample_fps', Math.round(parseFloat(displayVals.video_sample_fps ?? '6') * 10))}
              />
              <NumberRow label="Frames per segment" min={8} max={32} step={1}
                description="Frames per classification segment (~2.67s at 6fps). Must match model training. (default: 16)"
                displayValue={displayVals.frames_per_segment ?? '16'} saving={savingKey === 'frames_per_segment'} saved={savedKey === 'frames_per_segment'}
                onDisplayChange={v => setDisplayVals(d => ({ ...d, frames_per_segment: v }))}
                onSave={() => save('frames_per_segment', parseInt(displayVals.frames_per_segment ?? '16', 10))}
              />
              <NumberRow label="Processing resolution" unit="px" min={128} max={512} step={32}
                description="JEPA model input resolution in pixels. Lower = faster, higher = more accurate. (default: 256)"
                displayValue={displayVals.process_resolution ?? '256'} saving={savingKey === 'process_resolution'} saved={savedKey === 'process_resolution'}
                onDisplayChange={v => setDisplayVals(d => ({ ...d, process_resolution: v }))}
                onSave={() => save('process_resolution', parseInt(displayVals.process_resolution ?? '256', 10))}
              />
              <NumberRow label="Mask confidence threshold" min={0.01} max={0.99} step={0.05}
                description="SAM3 binarization threshold (0–1). Higher = fewer but more confident masks. (default: 0.50)"
                displayValue={displayVals.mask_confidence_threshold ?? '0.50'} saving={savingKey === 'mask_confidence_threshold'} saved={savedKey === 'mask_confidence_threshold'}
                onDisplayChange={v => setDisplayVals(d => ({ ...d, mask_confidence_threshold: v }))}
                onSave={() => save('mask_confidence_threshold', Math.round(parseFloat(displayVals.mask_confidence_threshold ?? '0.5') * 100))}
              />
            </div>

            {/* ── Narrative settings ── */}
            <div style={{ background: c.bgSecondary, border: `1px solid ${c.border}`, borderRadius: 3, padding: '20px 24px', marginBottom: 20 }}>
              <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, letterSpacing: '0.12em',
                textTransform: 'uppercase' as const, color: c.signal, marginBottom: 16 }}>
                Narrative Settings
              </div>

              {/* Narrative enabled toggle */}
              <div style={{ display: 'flex', alignItems: 'flex-start', padding: '14px 0', borderBottom: `1px solid ${c.border}`, gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: c.textPrimary, marginBottom: 4 }}>
                    Narrative Enabled
                    {!narrativeOn && (
                      <span style={{ fontSize: 10, fontFamily: '"JetBrains Mono", monospace', letterSpacing: '0.06em',
                        textTransform: 'uppercase' as const, background: 'rgba(220,38,38,0.15)', color: '#F87171',
                        padding: '2px 8px', borderRadius: 3, marginLeft: 8 }}>disabled</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: c.textMuted, lineHeight: 1.5 }}>
                    Enable AI narrative descriptions (replaces YES/NO badge). Kill switch — disable to fall back to silent mode.
                  </div>
                </div>
                <Toggle checked={narrativeOn} onChange={toggleNarrative} />
              </div>

              <NumberRow label="Detection Threshold" unit="%" min={0} max={100} step={1}
                description="Minimum confidence % for a detection to trigger narrative (default: 50%)"
                displayValue={displayVals.narrative_threshold ?? '50'} saving={savingKey === 'narrative_threshold'} saved={savedKey === 'narrative_threshold'}
                onDisplayChange={v => setDisplayVals(d => ({ ...d, narrative_threshold: v }))}
                onSave={() => save('narrative_threshold', parseInt(displayVals.narrative_threshold ?? '50', 10))}
              />
              <NumberRow label="Update Frequency" unit="frames" min={8} max={32} step={1}
                description="Frames between narrative updates (16 = one JEPA clip ≈ 8s at typical speed)"
                displayValue={displayVals.narrative_stride_frames ?? '16'} saving={savingKey === 'narrative_stride_frames'} saved={savedKey === 'narrative_stride_frames'}
                onDisplayChange={v => setDisplayVals(d => ({ ...d, narrative_stride_frames: v }))}
                onSave={() => save('narrative_stride_frames', parseInt(displayVals.narrative_stride_frames ?? '16', 10))}
              />
            </div>

            {/* ── Upload / quota settings ── */}
            <div style={{ background: c.bgSecondary, border: `1px solid ${c.border}`, borderRadius: 3, padding: '20px 24px' }}>
              <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, letterSpacing: '0.12em',
                textTransform: 'uppercase' as const, color: c.signal, marginBottom: 16 }}>
                Upload Limits & Quotas
              </div>
              <NumberRow label="Max upload size" unit="MB" min={1} max={200} step={1}
                description="Maximum file size accepted by the upload endpoint. (default: 20 MB)"
                displayValue={displayVals.max_upload_bytes ?? '20'} saving={savingKey === 'max_upload_bytes'} saved={savedKey === 'max_upload_bytes'}
                onDisplayChange={v => setDisplayVals(d => ({ ...d, max_upload_bytes: v }))}
                onSave={() => save('max_upload_bytes', parseInt(displayVals.max_upload_bytes ?? '20', 10) * 1024 * 1024)}
              />
              <NumberRow label="Free plan max duration" unit="sec" min={10} max={600} step={10}
                description="Maximum video duration for Free plan users. (default: 60s)"
                displayValue={displayVals.free_max_duration_seconds ?? '60'} saving={savingKey === 'free_max_duration_seconds'} saved={savedKey === 'free_max_duration_seconds'}
                onDisplayChange={v => setDisplayVals(d => ({ ...d, free_max_duration_seconds: v }))}
                onSave={() => save('free_max_duration_seconds', parseInt(displayVals.free_max_duration_seconds ?? '60', 10))}
              />
              <NumberRow label="Pro plan max duration" unit="sec" min={60} max={3600} step={60}
                description="Maximum video duration for Pro plan users. (default: 1200s = 20min)"
                displayValue={displayVals.pro_max_duration_seconds ?? '1200'} saving={savingKey === 'pro_max_duration_seconds'} saved={savedKey === 'pro_max_duration_seconds'}
                onDisplayChange={v => setDisplayVals(d => ({ ...d, pro_max_duration_seconds: v }))}
                onSave={() => save('pro_max_duration_seconds', parseInt(displayVals.pro_max_duration_seconds ?? '1200', 10))}
              />
              <NumberRow label="Demo analyses per day" min={1} max={20} step={1}
                description="Max analyses per IP per day for unauthenticated users. (default: 3)"
                displayValue={displayVals.demo_analyses_per_day ?? '3'} saving={savingKey === 'demo_analyses_per_day'} saved={savedKey === 'demo_analyses_per_day'}
                onDisplayChange={v => setDisplayVals(d => ({ ...d, demo_analyses_per_day: v }))}
                onSave={() => save('demo_analyses_per_day', parseInt(displayVals.demo_analyses_per_day ?? '3', 10))}
              />
            </div>

            {/* ── Danger zone ── */}
            <div style={{ background: c.bgSecondary, border: '1px solid rgba(220,38,38,0.3)', borderRadius: 3, padding: '20px 24px', marginTop: 20 }}>
              <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: '#DC2626', marginBottom: 12 }}>
                Inference Server — Danger Zone
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: c.textPrimary, marginBottom: 4 }}>Flush inference queue</div>
                  <div style={{ fontSize: 12, color: c.textMuted, lineHeight: 1.5 }}>
                    Kills all running and queued inference jobs immediately. Use when the server is stuck or processing stale jobs.
                    The model stays loaded — server is ready for new jobs instantly.
                  </div>
                </div>
                <button onClick={handleFlush} disabled={flushing}
                  style={{ background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.4)', color: '#F87171',
                    borderRadius: 3, padding: '8px 18px', fontSize: 12, fontWeight: 600, cursor: flushing ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap' as const, flexShrink: 0, opacity: flushing ? 0.6 : 1 }}>
                  {flushing ? 'Flushing…' : 'Flush Queue'}
                </button>
              </div>
              {flushResult && (
                <div style={{ marginTop: 10, fontSize: 12, color: flushResult.startsWith('✓') ? '#4ADE80' : '#F87171',
                  fontFamily: '"JetBrains Mono", monospace' }}>
                  {flushResult}
                </div>
              )}
            </div>

            <p style={{ fontSize: 11, color: c.textMuted, marginTop: 12, fontFamily: '"JetBrains Mono", monospace' }}>
              Changes persist to Supabase app_settings. Inference params take effect on next analysis. Upload limits immediate.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
